import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  Injector,
  model,
  NgZone,
  signal,
  untracked,
  viewChild,
} from '@angular/core';

import { EskoAgentWindowService } from '@shared/modules/esko-agent/services/esko-agent-window.service';
import { MatButton, MatIconButton } from '@angular/material/button';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatIcon } from '@angular/material/icon';
import { BasicModule } from '@shared/modules/basic.module';
import { MatFormField, MatInput, MatPrefix, MatSuffix } from '@angular/material/input';
import { CdkPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { OverlayContainer } from '@angular/cdk/overlay';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'esko-agent-bubble',
  templateUrl: './esko-agent-bubble.component.html',
  styleUrls: ['./esko-agent-bubble.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconButton,
    DragDropModule,
    MatIcon,
    BasicModule,
    MatButton,
    MatFormField,
    MatInput,
    MatPrefix,
    MatSuffix,
    CdkPortal,
    FormsModule,
    MatTooltip,
  ],
})
export class EskoAgentBubbleComponent {
  readonly eaw = inject(EskoAgentWindowService);

  readonly oc = inject(OverlayContainer);

  readonly inj = inject(Injector);

  readonly doc = inject<Document>(DOCUMENT);

  private outlet?: DomPortalOutlet;

  private stickyOutlet?: DomPortalOutlet;

  private stickyContainer?: HTMLElement;

  readonly portal = viewChild.required(CdkPortal, { read: CdkPortal });

  readonly dragging = signal(false);

  readonly startWindowSize = signal(0);

  readonly useChatStartInput = computed(() => this.eaw.useChatStartInput());

  readonly chatStarters = computed(() => this.eaw.chatStarters());

  readonly isWindowOpen = computed(() => this.eaw.isWindowOpen());

  readonly showBubble = computed(() => !!this.eaw.window() && !!this.eaw.ctx()?.canActivate());

  readonly stickToBottom = computed(() => !!this.eaw.ctx()?.stickToBottom());

  readonly startWindow = viewChild('startWindow', { read: ElementRef });

  readonly suggestionButtons = viewChild('suggestionButtons', { read: ElementRef });

  readonly marqueeContent = viewChild('marqueeContent', { read: ElementRef });

  readonly chatButton = viewChild('chatButton', { read: ElementRef });

  readonly scrollContainerHasScroll = signal<boolean>(false);

  readonly hasRecentChatActive = computed(() => this.eaw.ctx()?.messages()?.length > 0);

  private readonly zone = inject(NgZone);

  private scrollContainerCheckTimeout: ReturnType<typeof setTimeout> | null = null;

  readonly mutationObserver = this.zone.runOutsideAngular(
    () => new MutationObserver(() => this.scheduleScrollContainerCheck()),
  );

  readonly resizeObserver = this.zone.runOutsideAngular(
    () => new ResizeObserver(() => this.scheduleScrollContainerCheck()),
  );

  startQuestion = model('');

  constructor() {
    effect(() => {
      const startWindow = this.startWindow()?.nativeElement;

      if (startWindow) {
        this.startQuestion();
        this.chatStarters();

        this.startWindowSize.set(startWindow.offsetHeight);
      }
    });

    effect(() => {
      const showBubble = this.showBubble();
      const stickToBottom = this.stickToBottom();
      const useChatStartInput = this.useChatStartInput();
      const scrollContainer = this.eaw.ctx()?.getStartChatContainer();

      if (showBubble && useChatStartInput && !stickToBottom) {
        if (scrollContainer) {
          scrollContainer.style.paddingBottom = `${this.startWindowSize()}px`;
        }
      } else if (scrollContainer) {
        scrollContainer.style.paddingBottom = null;
      }
    });

    effect(() => {
      this.eaw.ctx();

      if (this.eaw.stickToBottom()) {
        this.stick();
      } else {
        this.float();
      }
    });

    effect(() => {
      const scrollContainer = this.eaw.ctx()?.getStartChatContainer();

      untracked(() => {
        this.mutationObserver.disconnect();
        this.resizeObserver.disconnect();

        if (scrollContainer) {
          this.mutationObserver.observe(scrollContainer, { childList: true, subtree: true });
          this.resizeObserver.observe(scrollContainer);
        }
      });
    });

    inject(DestroyRef).onDestroy(() => {
      this.mutationObserver.disconnect();
      this.resizeObserver.disconnect();
      clearTimeout(this.scrollContainerCheckTimeout);
    });

    effect((onCleanup) => {
      const button = this.chatButton()?.nativeElement as HTMLElement;

      if (!button) {
        return;
      }

      const updateButtonWidth = () => this.eaw.setChatButtonWidth(button.getBoundingClientRect().width);

      updateButtonWidth();

      const observer = new ResizeObserver(updateButtonWidth);
      observer.observe(button);

      onCleanup(() => observer.disconnect());
    });
  }

  onDragEnded(): void {
    setTimeout(() => this.dragging.set(false));
  }

  toggleChat(): void {
    if (!this.dragging()) {
      this.eaw.toggleWindow();
    }
  }

  startChat(message?: string): void {
    message ||= this.startQuestion();

    if (message) {
      this.startQuestion.set('');
      this.eaw.startSessionWithMessage(message);
    }
  }

  onKeydownEnter(event: KeyboardEvent): void {
    if (!event.shiftKey) {
      event.preventDefault();
      this.setStickToBottom(false);
      this.startChat();
    }
  }

  setStickToBottom(stick: boolean): void {
    this.eaw.ctx()?.stickToBottom.set(stick);
  }

  checkOverflow() {
    const container = this.suggestionButtons()?.nativeElement;
    const content = this.marqueeContent()?.nativeElement;

    if (container && content) {
      const isOverflowing = content.scrollWidth > container.clientWidth;

      container.setAttribute('style', `--gap-width: ${content.scrollWidth - container.clientWidth}px`);
      container.setAttribute('data-overflow', isOverflowing ? 'true' : 'false');
    }
  }

  onFileInputChange(fileInput: HTMLInputElement): void {
    const file = fileInput.files?.item(0);

    if (file) {
      this.eaw.onFileSelected(file);
    }

    fileInput.value = '';
  }

  onClearFile(): void {
    this.eaw.clearAttachedFile();
  }

  private scheduleScrollContainerCheck(): void {
    if (this.scrollContainerCheckTimeout == null) {
      this.scrollContainerCheckTimeout = setTimeout(() => {
        this.scrollContainerCheckTimeout = null;
        this.checkScrollContainer();
      }, 100);
    }
  }

  private checkScrollContainer(): void {
    const container = this.eaw.ctx()?.getStartChatContainer();

    if (container) {
      this.scrollContainerHasScroll.set(container.scrollHeight > container.offsetHeight);
    }
  }

  private float(): void {
    this.stickyOutlet?.detach();

    if (!this.outlet) {
      this.createOutlet('outlet', this.oc.getContainerElement());
    }

    if (!this.outlet.hasAttached()) {
      this.outlet.attachTemplatePortal(this.portal());
    }
  }

  private stick(): void {
    this.outlet?.detach();

    const container = this.eaw.ctx()?.getStartChatContainer();

    if (container !== this.stickyContainer) {
      if (this.stickyContainer) {
        this.disposeOutlet('stickyOutlet', this.stickyContainer);
        this.stickyContainer = container;
      }

      this.createOutlet('stickyOutlet', container);
    }

    this.stickyContainer = container;

    if (!this.stickyOutlet.hasAttached()) {
      this.stickyOutlet.attachTemplatePortal(this.portal());
    }
  }

  private disposeOutlet(outlet: 'outlet' | 'stickyOutlet', container: HTMLElement): void {
    container.removeChild(container.querySelector('.zef-chat-outlet-pane'));
    this[outlet]?.dispose();
  }

  private createOutlet(outlet: 'outlet' | 'stickyOutlet', container: HTMLElement): void {
    this[outlet]?.dispose();

    const pane = this.doc.createElement('div');
    pane.className = 'zef-chat-outlet-pane';
    container.appendChild(pane);

    this[outlet] = new DomPortalOutlet(pane, this.inj.get(ApplicationRef), this.inj);
  }
}
